import React from 'react';

import styled from '@emotion/styled';
import { Button } from '@lightspeed/flame/Button';
import { Box, FlameTheme, Flex } from '@lightspeed/flame/Core';
import { Radio } from '@lightspeed/flame/Radio';
import { Text } from '@lightspeed/flame/Text';
import { Tooltip } from '@lightspeed/flame/Tooltip';

import Format from 'app/src/data/Format';
import SaleManager from 'app/src/payments/SaleManager';
import { trackAnalyticsEvent } from 'helpers/AnalyticsTracking';
import { hasFeature } from 'root/api/admin/features';
import Decimal from 'root/apps/app/src/data/base/Decimal';
import getCardBrandIcon from 'root/apps/app/src/payments/CardBrandIcons';
import { translate } from 'root/apps/pages/utils/translate';
import {
  CNP_EVENT,
  initLspayMonitoring,
  INTEGRATION_TYPE,
  type LspayTerminalMonitoring,
  PAYMENT_STATUS,
} from 'root/apps/payments/utils/lspayMonitoring';
import Modules from 'shared_modules/Modules';

interface SavedCard {
  remoteCardRef: string;
  brand: string;
  last4: string;
  isDefault: boolean;
  isExpired: boolean;
  isDefaultSelected: boolean;
  isAvailable: boolean;
  nickname?: string | null;
}

interface PaymentSavedCardsProps {
  viewData: {
    savedCards: SavedCard[]; // Array of saved cards
    balance: Decimal; // Replace with actual type for saleData
    onSavedCardsFinished: () => void; // Callback for when the saved cards view is cancelled
    pli: string;
  };
  saleManager: SaleManager;
}

function Expired() {
  return (
    <Text color="#de2e37" fontSize="12px" style={{ marginTop: 0 }}>
      {translate('Expired')}
    </Text>
  );
}

function Default() {
  return (
    <Text color="#5c626a" fontSize="12px" style={{ marginTop: 0 }}>
      {translate('Default')}
    </Text>
  );
}

const MiddleAligned = styled(Box)`
  label {
    align-items: center;

    > div:first-of-type {
      display: flex;
      align-items: center;
    }
  }
`;

function SavedCardRadio({
  savedCard,
  hasNicknameFeature,
}: {
  savedCard: SavedCard;
  hasNicknameFeature: boolean;
}) {
  const Icon = getCardBrandIcon(savedCard.brand);

  return (
    <MiddleAligned mb="5px" p="2px">
      <Radio
        data-test="saved-card-radio"
        name="saved-card"
        disabled={savedCard.isExpired || !savedCard.isAvailable}
        defaultChecked={savedCard.isDefaultSelected}
        defaultValue={savedCard.remoteCardRef}
        label={
          <>
            <Icon size="24px" />
            <Box fontWeight="normal" pl="10px">
              <var>{savedCard.brand}</var> •••• <var>{savedCard.last4}</var>
              {savedCard.isExpired && <Expired />}
              {savedCard.isDefault && <Default />}
              {hasNicknameFeature && savedCard.nickname && (
                <Text fontSize="12px" color="#777777" style={{ marginTop: 0 }}>
                  {savedCard.nickname}
                </Text>
              )}
            </Box>
          </>
        }
      />
    </MiddleAligned>
  );
}

export default function PaymentSavedCards({
  viewData: { savedCards, balance, onSavedCardsFinished, pli },
  saleManager,
}: PaymentSavedCardsProps) {
  const amountInputRef = React.useRef<HTMLInputElement>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [hasNicknameFeature, setHasNicknameFeature] = React.useState(false);

  const monitoringRef = React.useRef<LspayTerminalMonitoring | null>(null);
  if (!monitoringRef.current && pli) {
    monitoringRef.current = initLspayMonitoring(pli, INTEGRATION_TYPE.SAVED_PAYMENT_METHOD);
  }

  React.useEffect(() => {
    hasFeature('CustomerCardOnFileNickname').then(setHasNicknameFeature);
  }, []);

  const defaultSelectedCardRemoteRef =
    savedCards.find((card) => card.isDefaultSelected)?.remoteCardRef || null;
  const [selectedCardRemoteRef, setSelectedCardRemoteRef] = React.useState<string | null>(null);

  const handleAmountBlur = () => {
    const paymentAmount = amountInputRef.current?.value;

    let amount = new Decimal(NaN);

    try {
      amount = new Decimal(paymentAmount?.toString() || '');

      if (!paymentAmount) {
        amount = new Decimal(0);
      } else if (amount.isNegative()) {
        amount = amount.negated();
      } else if (amount.isGreaterThan(balance)) {
        amount = balance;
      }
    } catch (err) {
      amount = new Decimal(NaN);
    }

    if (amountInputRef.current) {
      amountInputRef.current.value = Format.formatMoney(amount, 0, false);
    }
  };

  const handleSelectedCardChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCardRemoteRef(event.target.value);
  };

  const handleCancelClick = () => {
    onSavedCardsFinished();
  };

  const handleChargeClick = async () => {
    if (isSaving) return; // Prevent multiple submissions
    setIsSaving(true);

    // Get either the selected card or the default card if none is selected
    const remoteCardRef = selectedCardRemoteRef || defaultSelectedCardRemoteRef;

    // If no card is selected, we cannot proceed
    if (!remoteCardRef) return;
    const rawAmount = amountInputRef.current?.value ?? '0';
    const amountInCents = Math.round(parseFloat(rawAmount) * 100);

    // Prevent charging $0 amounts
    if (amountInCents === 0) {
      window.alertUser(translate('Please enter a proper charge amount'));
      setIsSaving(false);
      return;
    }

    if (Modules.hasModule('customer.card_minimum_fifty_cents')) {
      // Validate minimum amount
      if (amountInCents < 50) {
        window.alertUser(
          translate('Payment amount must be at least $0.50 for the selected payment method.')
        );
        setIsSaving(false);
        return;
      }
    }

    monitoringRef.current?.sendEvent(CNP_EVENT.START);

    const saleID = saleManager.sale.id;
    const attachedCustomerId = saleManager.customer.id;

    const chargeResponse = await fetch(`/admin/sale/${saleID}/payments/savedcof`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: amountInCents,
        customer_ref: attachedCustomerId.toString(),
        remote_card_ref: remoteCardRef,
      }),
    });

    if (chargeResponse.status !== 200) {
      monitoringRef.current?.sendEvent(CNP_EVENT.COMPLETE, {
        payment: { status: PAYMENT_STATUS.ERROR },
      });
      trackAnalyticsEvent({
        eventCategory: 'cof',
        eventName: 'pf',
        eventAction: 'pay_via_sale',
        eventLabel: 'error',
      });
      window.alertUser(translate('Cannot charge this saved card. Please try another card.'));
    } else {
      monitoringRef.current?.sendEvent(CNP_EVENT.COMPLETE, {
        payment: { status: PAYMENT_STATUS.APPROVED },
      });
      trackAnalyticsEvent({
        eventCategory: 'cof',
        eventName: 'pay',
        eventAction: 'pay_via_sale',
        eventLabel: 'success',
      });
    }

    saleManager.refreshSale();
    onSavedCardsFinished();
  };

  return (
    <FlameTheme>
      <Flex alignItems="center" mb="18px" mt="10px">
        <Text pr="12px" textTransform="uppercase" fontWeight="bold" color="#589141" fontSize="14px">
          {translate('Charge')}
        </Text>
        <div className="money-field-container payment-block-money localized" currency-symbol="$">
          <input
            type="text"
            className="money"
            size={10}
            defaultValue={Format.formatMoney(balance, 0, false)}
            data-test="savedCardPayment"
            id="saved_card_payment"
            name="saved_card_payment"
            title={translate('Payment amount')}
            autoComplete="off"
            ref={amountInputRef}
            onBlur={handleAmountBlur}
          />
        </div>
      </Flex>
      <Box mb="9px">
        <Text textTransform="uppercase" fontWeight="bold" fontSize="12px" letterSpacing="1.5px">
          {translate('Select a saved payment method')}
        </Text>
      </Box>
      <div onChange={handleSelectedCardChange}>
        {savedCards.map((card) => {
          if (!card.isAvailable) {
            // If the card is not available, we show it in a tooltip
            return (
              <Tooltip
                key={`saved-card-${card.brand}-${card.last4}`}
                light={true}
                content={translate('Card not accepted at this location')}
                placement="top-start"
              >
                <SavedCardRadio savedCard={card} hasNicknameFeature={hasNicknameFeature} />
              </Tooltip>
            );
          }
          // If the card is available, we show it normally
          return (
            <SavedCardRadio
              key={`saved-card-${card.brand}-${card.last4}`}
              savedCard={card}
              hasNicknameFeature={hasNicknameFeature}
            />
          );
        })}
      </div>
      <Box mt="15px" mb="10px">
        <Button
          style={{ fontWeight: 'normal', marginRight: '4px' }}
          onClick={handleCancelClick}
          disabled={isSaving}
        >
          {translate('Cancel')}
        </Button>
        <Button
          onClick={handleChargeClick}
          disabled={!defaultSelectedCardRemoteRef && !selectedCardRemoteRef}
          loading={isSaving}
        >
          {translate('Charge')}
        </Button>
      </Box>
    </FlameTheme>
  );
}
